import { PersonAddAlt1 } from "@mui/icons-material";
import {
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import { useIsMutating } from "@tanstack/react-query";
import classNames from "classnames";
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import CustomerDialog from "../../pages/PosPage/components/CustomerDialog/CustomerDialog";
import SmallTablePlaceholder from "../SmallTablePlaceholder";
import clear from "./clear.svg";
import { useStyles } from "./styles";

import { PosContext } from "../../shared/PosContext";

const AddPaymentWidget = ({
    selectedTip,
    selectedDiscount,
    onUpdateTip,
    onUpdateDiscount,
    onConfirm,
    handlePropertyChange,
    isCreatingInvoice,
    backCallback,
    isLoading,
    setPaymentType,
    presetDiscount,
    presetDiscountAmount,
    orderOnHouse,
    setOrderOnHouse,
    setSelectedCustomer,
    selectedCustomer,
    payingAll,
    order,
    invoice,
    fixedDiscountAmount,
    setFixedDiscountAmount,
    discountPercentage,
    setDiscountPercentage,
}) => {
    const classes = useStyles();

    const { refetchCustomers, refetchAddresses, refetchTablesData } =
        useContext(PosContext);

    const isMutating = useIsMutating();

    const [internalMessage, setInternalMessage] = useState(null);
    const [dollarRate, setDollarRate] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [changeCustomerDialogOpen, setChangeCustomerDialogOpen] =
        useState(false);

    // New state for partial payments
    const [cashAmount, setCashAmount] = useState(0);
    const [creditAmount, setCreditAmount] = useState(0);
    const [activePaymentType, setActivePaymentType] = useState("cash"); // Which input is active
    const [currentInput, setCurrentInput] = useState("0"); // Current calculator display
    const [selectedButton, setSelectedButton] = useState("");

    // New state for fixed discount amount
    const [discountType, setDiscountType] = useState("percentage"); // "percentage" or "fixed"

    // Effect to set fixed discount amount when preset discount amount exists
    // But only for full order payments, not partial payments
    useEffect(() => {
        if (invoice) {
            setFixedDiscountAmount(invoice.discount_amount);
            setDiscountPercentage(invoice.discount_percentage);
        }
    }, [invoice]);

    // New state to track if amounts were set via "Order amount" click
    const [cashSetViaOrderAmount, setCashSetViaOrderAmount] = useState(false);
    const [creditSetViaOrderAmount, setCreditSetViaOrderAmount] =
        useState(false);

    // Local guard to prevent rapid double-click submissions
    const confirmClickLockedRef = useRef(false);

    const tables = [
        {
            name: "Table 1",
            floor: "1",
            actions: [],
            canDoBack: true,
        },
    ];

    // Calculate the proper discounted amount
    const finalOrderAmount = useMemo(() => {
        if (invoice) {
            let invoiceTotal = invoice.grand_total;

            if (invoice.discount_amount > 0) {
                return invoiceTotal;
            }

            if (fixedDiscountAmount > 0) {
                return invoiceTotal - fixedDiscountAmount;
            }

            if (discountPercentage > 0) {
                return Math.ceil(invoiceTotal - (invoiceTotal * discountPercentage) / 100);
            }

            return invoiceTotal;
        }
        return 0;
    }, [invoice, fixedDiscountAmount, discountPercentage]);

    // Calculate totals and remaining amount
    const totalPaid = cashAmount + creditAmount;
    const remainingAmount = Math.max(0, finalOrderAmount - totalPaid);
    const changeAmount = Math.max(0, totalPaid - finalOrderAmount);

    const userCanGiveDiscount = () => {
        return true;
    };

    // Enhanced keyboard support
    const handleKeyboardInput = useCallback(
        (event) => {
            // Avoid intercepting typing when focused inside any input fields or editable areas
            const target = event.target;
            const tagName = target?.tagName?.toLowerCase?.();
            const isTypingInField =
                tagName === "input" ||
                tagName === "textarea" ||
                target?.isContentEditable;
            if (isTypingInField) {
                return;
            }

            const key = event.key;

            // Prevent default behavior for number keys and specific keys
            if (
                /[0-9]/.test(key) ||
                ["Backspace", "Delete", "Enter", "Escape"].includes(key)
            ) {
                event.preventDefault();
            }

            if (/[0-9]/.test(key)) {
                handleUpdateAmount(parseInt(key));
            } else if (key === "Backspace" || key === "Delete") {
                handleUpdateClear();
            } else if (key === "Escape") {
                handleClearAll();
            } else if (key === "Enter") {
                // TODO: suspected to make repeated calls, not sure yet.
                // validateInput();
            }
        },
        [
            currentInput,
            activePaymentType,
            cashAmount,
            creditAmount,
            totalPaid,
            finalOrderAmount,
            orderOnHouse,
            selectedButton,
            selectedCustomer,
        ]
    );

    useEffect(() => {
        // Add keyboard event listener
        window.addEventListener("keydown", handleKeyboardInput);

        return () => {
            window.removeEventListener("keydown", handleKeyboardInput);
        };
    }, [handleKeyboardInput]);

    // Enhanced calculator functions
    const handleUpdateAmount = (digit) => {
        let newValue = currentInput;

        // If current input is "0", replace it; otherwise append
        if (newValue === "0") {
            newValue = digit.toString();
        } else {
            newValue += digit.toString();
        }

        setCurrentInput(newValue);
        const amount = parseFloat(newValue) || 0;
        updateActivePaymentAmount(amount, true); // Skip parent update for manual calculator input

        // Clear tracking flag if amount is different from order amount
        if (amount !== finalOrderAmount) {
            if (activePaymentType === "cash") {
                setCashSetViaOrderAmount(false);
            } else {
                setCreditSetViaOrderAmount(false);
            }
        }
    };

    const handleReplaceAmount = (amount) => {
        // Always use current discounted amount when called from order amount click
        const actualAmount =
            amount === finalOrderAmount ? finalOrderAmount : amount;
        const newValue = actualAmount.toString();
        setCurrentInput(newValue);

        // Skip parent update when setting amount via "Order amount" to prevent feedback loop
        const isOrderAmountClick = actualAmount === finalOrderAmount;
        updateActivePaymentAmount(actualAmount, isOrderAmountClick);

        // When setting via "Order amount" click, reset the other payment method and track this one
        if (actualAmount === finalOrderAmount) {
            if (activePaymentType === "cash") {
                setCashSetViaOrderAmount(true);
                // Reset credit if it was also set via order amount
                if (creditSetViaOrderAmount) {
                    setCreditAmount(0);
                }
                setCreditSetViaOrderAmount(false);
            } else {
                setCreditSetViaOrderAmount(true);
                // Reset cash if it was also set via order amount
                if (cashSetViaOrderAmount) {
                    setCashAmount(0);
                    // Don't call handlePropertyChange here to avoid affecting parent calculation
                    // The credit amount will be set correctly via updateActivePaymentAmount
                }
                setCashSetViaOrderAmount(false);
            }
        }
    };

    const handleUpdateClear = () => {
        let newValue = currentInput;

        if (newValue.length <= 1) {
            newValue = "0";
        } else {
            newValue = newValue.slice(0, -1);
        }

        setCurrentInput(newValue);
        const amount = parseFloat(newValue) || 0;
        updateActivePaymentAmount(amount, true); // Skip parent update for manual calculator input

        // Clear tracking flag if amount is different from order amount
        if (amount !== finalOrderAmount) {
            if (activePaymentType === "cash") {
                setCashSetViaOrderAmount(false);
            } else {
                setCreditSetViaOrderAmount(false);
            }
        }
    };

    const handleClearAll = () => {
        setCurrentInput("0");
        updateActivePaymentAmount(0, true); // Skip parent update for manual keypad input
        // Reset tracking flags when clearing
        if (activePaymentType === "cash") {
            setCashSetViaOrderAmount(false);
        } else {
            setCreditSetViaOrderAmount(false);
        }
    };

    const updateActivePaymentAmount = (amount, skipParentUpdate = false) => {
        if (activePaymentType === "cash") {
            setCashAmount(amount);
            // Update the parent component's amount for backward compatibility
            // But skip this when amount is set via "Order amount" to prevent feedback loop
            if (!skipParentUpdate) {
                handlePropertyChange(null, "amount", amount.toString());
            }
        } else {
            setCreditAmount(amount);
        }
    };

    const handlePaymentTypeSwitch = (type) => {
        setActivePaymentType(type);
        // Update current input to show the amount for the selected payment type
        const amount = type === "cash" ? cashAmount : creditAmount;

        // If this payment type was set via "Order Amount", show the finalOrderAmount instead
        const wasSetViaOrderAmount =
            type === "cash" ? cashSetViaOrderAmount : creditSetViaOrderAmount;
        const displayAmount = wasSetViaOrderAmount ? finalOrderAmount : amount;

        setCurrentInput(displayAmount === 0 ? "0" : displayAmount.toString());
        setErrorMessage(null);
    };

    const validateInput = () => {
        // Check if total payment covers the order amount
        if (
            totalPaid < finalOrderAmount &&
            !orderOnHouse &&
            selectedButton !== "Deferred"
        ) {
            setErrorMessage(
                `Insufficient payment. Remaining: ₪${remainingAmount.toFixed(
                    2
                )}`
            );
            return false;
        }

        // Check if at least one payment method is used (unless special cases)
        if (!orderOnHouse && selectedButton !== "Deferred") {
            if (cashAmount === 0 && creditAmount === 0) {
                setErrorMessage("Please enter payment amount");
                return false;
            }
        } else if (selectedButton && selectedButton === "Deferred") {
            if (!selectedCustomer) {
                setErrorMessage("Customer Not Selected");
                return false;
            }
        }

        // Prepare payment mode data for the payload
        const modeOfPayment = {};
        if (cashAmount > 0) {
            modeOfPayment["Cash"] = cashAmount;
        }
        if (creditAmount > 0) {
            modeOfPayment["Credit Card"] = creditAmount;
        }

        // Update payment type for backward compatibility
        if (cashAmount > 0 && creditAmount > 0) {
            setPaymentType("Mixed");
        } else if (cashAmount > 0) {
            setPaymentType("Cash");
        } else if (creditAmount > 0) {
            setPaymentType("Credit Card");
        }

        if (onConfirm) {
            // Prepare discount information to pass along with payment data
            const discountInfo = {
                discountType: discountType,
                fixedDiscountAmount:
                    discountType === "fixed" ? fixedDiscountAmount : 0,
                percentageDiscount:
                    discountType === "percentage" ? selectedDiscount : 0,
            };

            // Pass the modeOfPayment and discount info to the parent component
            onConfirm(modeOfPayment, discountInfo);
        }
        return true;
    };

    useEffect(() => {
        // Fetch the dollar rate from an API
        fetch("https://api.exchangerate-api.com/v4/latest/USD")
            .then((response) => response.json())
            .then((data) => {
                setDollarRate(data.rates.ILS);
            })
            .catch((error) => {
                console.error("Error fetching dollar rate:", error);
            });
    }, []);

    // Auto-update payment amounts when discount changes if they were set via "Order amount" click
    useEffect(() => {
        if (cashSetViaOrderAmount) {
            setCashAmount(finalOrderAmount);
            // Update current input if cash is the active payment type
            if (activePaymentType === "cash") {
                setCurrentInput(finalOrderAmount.toString());
            }
        }
        if (creditSetViaOrderAmount) {
            setCreditAmount(finalOrderAmount);
            // Update current input if credit is the active payment type
            if (activePaymentType === "credit") {
                setCurrentInput(finalOrderAmount.toString());
            }
        }
    }, [
        finalOrderAmount,
        cashSetViaOrderAmount,
        creditSetViaOrderAmount,
        activePaymentType,
    ]);

    useEffect(() => {
        if (isCreatingInvoice) {
            setInternalMessage("Creating invoice please wait");
        } else {
            setInternalMessage(null);
        }
    }, [isCreatingInvoice]);

    // Release click lock when no submission is in-flight
    useEffect(() => {
        if ((isMutating || 0) === 0 && !isCreatingInvoice && !isLoading) {
            confirmClickLockedRef.current = false;
        }
    }, [isMutating, isCreatingInvoice, isLoading]);

    const handleBackClicked = () => {
        if (backCallback) {
            backCallback();
        }
    };

    const getPaymentTypeButtonStyle = (type) => ({
        flex: 1,
        backgroundColor: activePaymentType === type ? "#e14954" : "#6E7187",
        color: "#FFFFFF",
        marginRight: "5px",
        marginLeft: "5px",
        border:
            activePaymentType === type ? "2px solid #000" : "1px solid #ccc",
    });

    const getButtonStyle1 = (type) => ({
        flex: 1,
        backgroundColor: selectedButton === type ? "#e14954" : "#6E7187",
        color: selectedButton === type ? "#FFFFFF" : "#FFFFFF",
        marginRight: "10px",
        border: selectedButton === type ? "1px solid #000" : "1px solid #ccc",
    });

    const handleButtonChange = (event, newButton) => {
        setSelectedButton(newButton);
    };

    useEffect(() => {
        if (selectedButton && selectedButton === "Deferred") {
            setOrderOnHouse(false);
        } else if (selectedButton && selectedButton === "OTH") {
            setOrderOnHouse(true);
        } else {
            setOrderOnHouse(false);
        }
    }, [selectedButton]);

    const changeCustomer = (customer) => {
        setChangeCustomerDialogOpen(true);
    };

    const onConfirmCustomerChanged = (customerName, address) => {
        refetchCustomers();
        refetchAddresses();
        setSelectedCustomer(customerName);
        setErrorMessage(null);
        setChangeCustomerDialogOpen(false);
    };

    const onCustomerSelected = (customer, address) => {
        setSelectedCustomer(customer);
        setErrorMessage(null);
        setChangeCustomerDialogOpen(false);
    };

    const handleCustomerChangeClose = () => {
        setChangeCustomerDialogOpen(false);
    };

    // Handle fixed discount amount input change
    const handleFixedDiscountChange = (event) => {
        // Don't allow changes if this is a preset discount amount
        if (
            presetDiscountAmount != null &&
            presetDiscountAmount > 0
        ) {
            return;
        }

        const value =
            event.target.value === "" ? 0 : parseFloat(event.target.value) || 0;
        setFixedDiscountAmount(value);

        if (value > 0) {
            // Switch to fixed discount mode and clear percentage discount
            setDiscountType("fixed");
            if (onUpdateDiscount) {
                onUpdateDiscount(0); // Clear percentage discount
            }
        } else {
            // Switch back to percentage mode when fixed amount is 0 or empty
            setDiscountType("percentage");
            // Restore preset discount if it exists, otherwise keep current percentage
            if (presetDiscount != null && onUpdateDiscount) {
                onUpdateDiscount(presetDiscount); // Restore preset discount
            }
        }
    };

    // Handle percentage discount button click
    const handlePercentageDiscountClick = (percentage) => {
        // Don't allow percentage changes if there's a preset discount amount (when paying all)
        if (fixedDiscountAmount > 0) {
            return;
        }
        setDiscountPercentage(percentage);
    };

    return (
        <div
            className={classes.AddPaymentWidgetWrap}
            style={{
                height: "95vh",
                maxHeight: "95vh",
                display: "flex",
                flexDirection: "column",
                position: "relative",
            }}
        >
            <div
                className={classes.AddPaymentWidget}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                {/* Header - Fixed */}
                <div
                    className={classes.SmallTablePlaceholderWrapper}
                    style={{
                        flexShrink: 0,
                        zIndex: 1,
                    }}
                >
                    <SmallTablePlaceholder
                        backCallback={handleBackClicked}
                        tables={tables}
                        internalMessage={internalMessage}
                    />
                </div>

                {/* Scrollable Content Area */}
                <div
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        paddingBottom: "80px", // Extra space for footer
                        minHeight: 0,
                    }}
                >
                    {/* Top Row: Order Amount, Change, and Currency Rate */}
                    <div
                        style={{
                            display: "flex",
                            gap: "8px",
                            marginBottom: "15px",
                        }}
                    >
                        {/* Order Amount Section */}
                        <div
                            style={{
                                backgroundColor: "#f0f8ff",
                                border: "1px solid #b6d7ff",
                                borderRadius: "6px",
                                padding: "10px 12px",
                                flex: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: "pointer",
                                minHeight: "50px",
                            }}
                            onClick={() =>
                                handleReplaceAmount(finalOrderAmount)
                            }
                        >
                            <span
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#1e3a8a",
                                }}
                            >
                                Order Amount:
                            </span>
                            <span
                                style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                    color: "#1d4ed8",
                                }}
                            >
                                ₪{finalOrderAmount}
                            </span>
                        </div>

                        {/* Change Section */}
                        <div
                            style={{
                                backgroundColor: "#e8f5e8",
                                border: "1px solid #c3e6c3",
                                borderRadius: "6px",
                                padding: "10px 12px",
                                flex: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                minHeight: "50px",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#2d5a2d",
                                }}
                            >
                                Change:
                            </span>
                            <span
                                style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                    color: "#1e7e34",
                                }}
                            >
                                ₪{changeAmount.toFixed(2)}
                            </span>
                        </div>

                        {/* Currency Rate Mini Card */}
                        <div
                            style={{
                                backgroundColor: "#fff8e1",
                                border: "1px solid #ffcc02",
                                borderRadius: "6px",
                                padding: "8px 10px",
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: "50px",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    color: "#e65100",
                                    marginBottom: "2px",
                                }}
                            >
                                USD
                            </span>
                            <span
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    color: "#ef6c00",
                                }}
                            >
                                ₪{dollarRate?.toFixed(2) || "-.--"}
                            </span>
                        </div>
                    </div>

                    {/* Payment Summary (Horizontal) */}
                    <div
                        style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #e9ecef",
                            borderRadius: "6px",
                            padding: "12px",
                            marginBottom: "15px",
                        }}
                    >
                        <div
                            style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                marginBottom: "10px",
                                color: "#333",
                                borderBottom: "1px solid #dee2e6",
                                paddingBottom: "6px",
                                textAlign: "center",
                            }}
                        >
                            Payment Summary
                        </div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-around",
                                alignItems: "center",
                                gap: "10px",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    flex: 1,
                                }}
                            >
                                <span
                                    style={{
                                        color: "#666",
                                        fontSize: "12px",
                                        marginBottom: "3px",
                                    }}
                                >
                                    Cash
                                </span>
                                <span
                                    style={{
                                        fontWeight: "bold",
                                        color: "#333",
                                        fontSize: "16px",
                                    }}
                                >
                                    ₪{cashAmount.toFixed(2)}
                                </span>
                            </div>
                            <div
                                style={{
                                    width: "1px",
                                    height: "30px",
                                    backgroundColor: "#dee2e6",
                                }}
                            ></div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    flex: 1,
                                }}
                            >
                                <span
                                    style={{
                                        color: "#666",
                                        fontSize: "12px",
                                        marginBottom: "3px",
                                    }}
                                >
                                    Credit
                                </span>
                                <span
                                    style={{
                                        fontWeight: "bold",
                                        color: "#333",
                                        fontSize: "16px",
                                    }}
                                >
                                    ₪{creditAmount.toFixed(2)}
                                </span>
                            </div>
                            <div
                                style={{
                                    width: "1px",
                                    height: "30px",
                                    backgroundColor: "#dee2e6",
                                }}
                            ></div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    flex: 1,
                                }}
                            >
                                <span
                                    style={{
                                        color: "#666",
                                        fontSize: "12px",
                                        marginBottom: "3px",
                                    }}
                                >
                                    Total
                                </span>
                                <span
                                    style={{
                                        fontWeight: "bold",
                                        color: "#2c3e50",
                                        fontSize: "16px",
                                    }}
                                >
                                    ₪{totalPaid.toFixed(2)}
                                </span>
                            </div>
                            <div
                                style={{
                                    width: "1px",
                                    height: "30px",
                                    backgroundColor: "#dee2e6",
                                }}
                            ></div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    flex: 1,
                                }}
                            >
                                <span
                                    style={{
                                        color: "#666",
                                        fontSize: "12px",
                                        marginBottom: "3px",
                                    }}
                                >
                                    Remaining
                                </span>
                                <span
                                    style={{
                                        fontWeight: "bold",
                                        color:
                                            remainingAmount > 0
                                                ? "#e74c3c"
                                                : "#28a745",
                                        fontSize: "16px",
                                    }}
                                >
                                    ₪{remainingAmount.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className={classes.MainContainer}>
                        <div>
                            {/* Quick Amount Buttons */}
                            <div className={classes.Tips}>
                                <div className="Tips__Amounts">
                                    {[10, 20, 50, 100, 200].map((entry) => {
                                        return (
                                            <div
                                                key={entry}
                                                onClick={() =>
                                                    handleReplaceAmount(entry)
                                                }
                                                className={classNames(
                                                    "Number",
                                                    {
                                                        IsSelected:
                                                            selectedTip ===
                                                            entry,
                                                    }
                                                )}
                                            >
                                                {entry}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Calculator Keypad */}
                            <div className={classes.Keys}>
                                {[9, 8, 7, 6, 5, 4, 3, 2, 1, 0].map((entry) => {
                                    return (
                                        <div
                                            key={entry}
                                            className={classNames("Number")}
                                            onClick={() =>
                                                handleUpdateAmount(entry)
                                            }
                                        >
                                            {entry}
                                        </div>
                                    );
                                })}
                                <div className="Clear" onClick={handleClearAll}>
                                    C
                                </div>
                                <div
                                    className="Delete"
                                    onClick={handleUpdateClear}
                                >
                                    <img src={clear} alt="delete" />
                                </div>
                            </div>
                        </div>

                        <div className={classes.PaymentType}>
                            <div className={classes.VerticalContainer}>
                                {errorMessage && (
                                    <div
                                        style={{
                                            color: "red",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        {errorMessage}
                                    </div>
                                )}

                                {/* Payment Type Selection */}
                                <div style={{ marginBottom: "15px" }}>
                                    <div
                                        style={{
                                            fontSize: "14px",
                                            marginBottom: "8px",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        Active Payment Method:
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            width: "100%",
                                        }}
                                    >
                                        <Button
                                            onClick={() =>
                                                handlePaymentTypeSwitch("cash")
                                            }
                                            style={getPaymentTypeButtonStyle(
                                                "cash"
                                            )}
                                        >
                                            Cash (₪{cashAmount.toFixed(2)})
                                        </Button>
                                        <Button
                                            onClick={() =>
                                                handlePaymentTypeSwitch(
                                                    "credit"
                                                )
                                            }
                                            style={getPaymentTypeButtonStyle(
                                                "credit"
                                            )}
                                        >
                                            Credit (₪{creditAmount.toFixed(2)})
                                        </Button>
                                    </div>
                                </div>

                                {/* Current Input Display */}
                                <div style={{ marginBottom: "15px" }}>
                                    <TextField
                                        label={`${
                                            activePaymentType === "cash"
                                                ? "Cash"
                                                : "Credit Card"
                                        } Amount`}
                                        value={currentInput}
                                        variant="outlined"
                                        // fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    ₪
                                                </InputAdornment>
                                            ),
                                            readOnly: true,
                                            style: {
                                                fontSize: "18px",
                                                fontWeight: "bold",
                                            },
                                        }}
                                        style={{ backgroundColor: "#f5f5f5" }}
                                    />
                                </div>

                                {/* Discount Section */}
                                <div>
                                    {userCanGiveDiscount() && (
                                        <div className={classes.Discount}>
                                            {/* Fixed Amount Discount Input */}
                                            <div
                                                style={{ marginBottom: "15px" }}
                                            >
                                                <TextField
                                                    label="Fixed Discount Amount"
                                                    type="number"
                                                    value={
                                                        fixedDiscountAmount ||
                                                        ""
                                                    }
                                                    onChange={
                                                        handleFixedDiscountChange
                                                    }
                                                    variant="outlined"
                                                    // size="small"
                                                    disabled={
                                                        invoice.discount_amount >
                                                        0
                                                    }
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                ₪
                                                            </InputAdornment>
                                                        ),
                                                        style: {
                                                            fontSize: "18px",
                                                            fontWeight: "bold",
                                                        },
                                                    }}
                                                    inputProps={{
                                                        min: 0,
                                                        step: 0.01,
                                                    }}
                                                    style={{
                                                        backgroundColor:
                                                            presetDiscountAmount !=
                                                                null &&
                                                            presetDiscountAmount >
                                                                0 &&
                                                            payingAll
                                                                ? "#f0f0f0"
                                                                : "#f5f5f5",
                                                        // width: "100%",
                                                        // backgroundColor: "white"
                                                    }}
                                                    helperText={
                                                        presetDiscountAmount !=
                                                            null &&
                                                        presetDiscountAmount >
                                                            0 &&
                                                        payingAll
                                                            ? "Preset discount amount from table order"
                                                            : !payingAll
                                                            ? "Enter amount to discount selected items"
                                                            : discountType ===
                                                                  "fixed" &&
                                                              fixedDiscountAmount >
                                                                  0
                                                            ? "Fixed discount applied (overrides preset discount)"
                                                            : "Enter amount to use fixed discount"
                                                    }
                                                />
                                            </div>

                                            {/* Percentage Discount Buttons */}
                                            <div
                                                className={`Discount__Amounts ${
                                                    fixedDiscountAmount > 0
                                                        ? "Disabled"
                                                        : ""
                                                }`}
                                            >
                                                {[0, 5, 10, 15, 20].map(
                                                    (entry) => {
                                                        return (
                                                            <div
                                                                key={entry}
                                                                onClick={() =>
                                                                    fixedDiscountAmount >
                                                                    0
                                                                        ? console.log(
                                                                              "Discount mode disabled or preset amount exists"
                                                                          )
                                                                        : handlePercentageDiscountClick(
                                                                              entry
                                                                          )
                                                                }
                                                                className={classNames(
                                                                    "Number",
                                                                    "DiscountButton",
                                                                    {
                                                                        IsSelected:
                                                                            discountPercentage ===
                                                                            entry,
                                                                    }
                                                                )}
                                                                style={{
                                                                    opacity:
                                                                        fixedDiscountAmount >
                                                                        0
                                                                            ? 0.5
                                                                            : 1,
                                                                    cursor:
                                                                        fixedDiscountAmount >
                                                                        0
                                                                            ? "not-allowed"
                                                                            : "pointer",
                                                                }}
                                                            >
                                                                {entry}%
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Special Payment Options */}
                                <div
                                    style={{ width: "100%", marginTop: "15px" }}
                                >
                                    <ToggleButtonGroup
                                        style={{
                                            width: "100%",
                                            display: "flex",
                                        }}
                                        value={selectedButton}
                                        exclusive
                                        onChange={handleButtonChange}
                                        aria-label="payment options"
                                    >
                                        <ToggleButton
                                            value="OTH"
                                            aria-label="OTH"
                                            style={getButtonStyle1("OTH")}
                                        >
                                            OTH
                                        </ToggleButton>
                                        <ToggleButton
                                            value="Deferred"
                                            aria-label="Deferred"
                                            style={getButtonStyle1("Deferred")}
                                        >
                                            Deferred
                                        </ToggleButton>
                                        {selectedButton &&
                                            selectedButton === "Deferred" && (
                                                <div>
                                                    <IconButton
                                                        className={
                                                            classes.Table_edit_customer
                                                        }
                                                        onClick={() =>
                                                            changeCustomer(
                                                                selectedCustomer
                                                            )
                                                        }
                                                    >
                                                        <PersonAddAlt1
                                                            fontSize="x-small"
                                                            color="white"
                                                        />
                                                    </IconButton>
                                                </div>
                                            )}
                                    </ToggleButtonGroup>
                                </div>

                                {selectedButton && (
                                    <div
                                        style={{
                                            marginTop: "10px",
                                            marginBottom: "20px",
                                        }}
                                    >
                                        {selectedButton === "OTH" && (
                                            <div>Order on House Selected</div>
                                        )}
                                        {selectedButton === "Deferred" && (
                                            <div>
                                                {selectedCustomer ? (
                                                    <div>
                                                        Payment for{" "}
                                                        {selectedCustomer}
                                                    </div>
                                                ) : (
                                                    <div>Select Customer</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Fixed Footer - Always Visible at Bottom */}

                <button
                    className="Btn--Primary"
                    onClick={() => {
                        if (confirmClickLockedRef.current) return;
                        confirmClickLockedRef.current = true;
                        const ok = validateInput();
                        if (!ok) {
                            // Unlock immediately if validation failed
                            confirmClickLockedRef.current = false;
                        }
                    }}
                    disabled={isMutating > 0 || isCreatingInvoice || isLoading}
                >
                    {isMutating > 0 || isCreatingInvoice || isLoading ? (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress
                                size={20}
                                style={{ marginRight: "10px", color: "white" }}
                            />
                            Confirming payment...
                        </div>
                    ) : (
                        "Confirm Payment & Print receipt"
                    )}
                </button>

                <CustomerDialog
                    dialogOpen={changeCustomerDialogOpen}
                    onCancel={handleCustomerChangeClose}
                    onConfirm={onConfirmCustomerChanged}
                    selectedCustomer={selectedCustomer}
                    customerSelected={onCustomerSelected}
                ></CustomerDialog>
            </div>
        </div>
    );
};

export default AddPaymentWidget;
